import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { checkAvailability } from '../../../helpers/service/public/register';
import { PHONE_CODE } from '../../../consts/common';
import { getUKPhoneWithoutFirstZero } from '../../../helpers/phone/phone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { createPortal } from 'react-dom';
import { SimpleModal } from '../../../components/SimpleModal/SimpleModal';

interface Props {
  onSubmit: (data) => void;
  onCancel: () => void;
  initialValues?: any;
  cooldownSec?: number;
  emailHint?: string;
}

export function PersonalDetailsForm(props: Props) {
  const { onSubmit } = props;

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isUnder13HelpOpen, setIsUnder13HelpOpen] = React.useState(false);

  const [gateError, setGateError] = React.useState<string | undefined>(undefined);
  const [gateCode, setGateCode] = React.useState<string | undefined>(undefined);
  const [isPhoneOptionalForRegistration, setIsPhoneOptionalForRegistration] = React.useState(false);

  const isEndpointDisabled = (payloadOrRes: any) => {
    const code = String(payloadOrRes?.code || payloadOrRes?.id || '');
    const httpStatus = Number(payloadOrRes?.httpStatus || payloadOrRes?.status || 0);
    return code === 'ENDPOINT_DISABLED' || httpStatus === 503;
  };

  const extractGateText = (payloadOrRes: any, fallback: string) =>
    payloadOrRes?.details?.text || payloadOrRes?.message || fallback;

  const turnstileTokenRef = React.useRef<string | undefined>(undefined);

  const [captchaReady, setCaptchaReady] = React.useState(false);
  const turnstileContainerRef = React.useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const sitekey = (window as any).turnstileSiteKey;
    if (!sitekey || !turnstileContainerRef.current) return;

    const tryRender = () => {
      if (!(window as any).turnstile) return;
      if (turnstileWidgetIdRef.current !== undefined) return;
      turnstileWidgetIdRef.current = (window as any).turnstile.render(turnstileContainerRef.current, {
        sitekey,
        callback: (token: string) => {
          turnstileTokenRef.current = token;
          setCaptchaReady(true);
        },
        'expired-callback': () => {
          turnstileTokenRef.current = undefined;
          setCaptchaReady(false);
        },
        'error-callback': () => {
          turnstileTokenRef.current = undefined;
          setCaptchaReady(false);
        }
      });
    };

    if ((window as any).turnstile) {
      tryRender();
    } else {
      (window as any).__pendingTurnstileInit = tryRender;
    }

    return () => {
      (window as any).__pendingTurnstileInit = undefined;
      if (turnstileWidgetIdRef.current !== undefined && (window as any).turnstile) {
        (window as any).turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = undefined;
      }
    };
  }, []);

  const handleSubmit = (data: any) => {
    onSubmit({
      ...data,
      captchaToken: turnstileTokenRef.current
    });
  };

  const defaultInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    phoneCode: PHONE_CODE.UK,
    terms: false,
    additionalTerms: false
  };

  const initialValues = props.initialValues || defaultInitialValues;
  const isCooldown = (props.cooldownSec || 0) > 0;

  const PersonalDetailsSchema = Yup.object().shape({
    firstName: Yup.string().required('Please enter your first name.'),
    lastName: Yup.string().required('Please enter your last name.'),
    password: Yup.string()
      .required('Please create a password.')
      .test(
        'password',
        'Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number.',
        value => {
          const passwordRegExp = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/);

          return value && passwordRegExp.test(value);
        }
      ),
    confirmPassword: Yup.string().test(
      'confirmPassword',
      'The passwords you entered do not match. Please try again.',
      function(value) {
        return value === this.parent.password;
      }
    ),
    email: Yup.string()
      .required('Please enter a valid email address.')
      .email('Please enter a valid email address')
      .test('email', 'This email address is already registered.', function(value) {
        if (!value) {
          setIsPhoneOptionalForRegistration(false);
          return false;
        }

        setGateError(undefined);
        setGateCode(undefined);

        return checkAvailability({ email: value })
          .then((res: any) => {
            if (res?.isError && isEndpointDisabled(res)) {
              setGateError(extractGateText(res, 'This service is temporarily unavailable. Please try again later.'));
              setGateCode(res?.code || 'ENDPOINT_DISABLED');
              return true;
            }
            setIsPhoneOptionalForRegistration(!!res?.isPhoneOptionalForRegistration);
            return !!res?.isAvailable;
          })
          .catch((e: any) => {
            const payload = e?.response?.data || e;

            if (isEndpointDisabled(payload)) {
              setGateError(
                extractGateText(payload, 'This service is temporarily unavailable. Please try again later.')
              );
              setGateCode(payload?.code || 'ENDPOINT_DISABLED');
              return true;
            }

            setGateError(
              extractGateText(payload, 'We cannot validate your details right now. Please try again later.')
            );
            return true;
          });
      }),
    phone: Yup.string()
      .test('phoneRequired', 'Please enter a valid phone number.', value => {
        if (isPhoneOptionalForRegistration && !value) return true;
        return !!value;
      })
      .test('phone', 'The phone number is either already registered or contains invalid characters.', function(value) {
        if (isPhoneOptionalForRegistration && !value) return true;

        const phoneRegExp = /^\d+$|^\d+#\d+$/;
        const phoneCode = this.parent.phoneCode;
        const phone = getUKPhoneWithoutFirstZero(value, phoneCode);

        const result = phoneRegExp.test(phone);
        if (!result) return false;

        const phoneForCheck = `${phoneCode}${phone}`;

        setGateError(undefined);
        setGateCode(undefined);

        return checkAvailability({ phone: phoneForCheck })
          .then((res: any) => {
            if (res?.isError && isEndpointDisabled(res)) {
              setGateError(extractGateText(res, 'This service is temporarily unavailable. Please try again later.'));
              setGateCode(res?.code || 'ENDPOINT_DISABLED');
              return true;
            }
            return !!res?.isAvailable;
          })
          .catch((e: any) => {
            const payload = e?.response?.data || e;

            if (isEndpointDisabled(payload)) {
              setGateError(
                extractGateText(payload, 'This service is temporarily unavailable. Please try again later.')
              );
              setGateCode(payload?.code || 'ENDPOINT_DISABLED');
              return true;
            }

            setGateError(
              extractGateText(payload, 'We cannot validate your details right now. Please try again later.')
            );
            return true;
          });
      })
      .test('phoneLength', 'Please enter a valid mobile phone number', value => {
        if (isPhoneOptionalForRegistration && !value) return true;
        if (value && value.length < 7) return false;
        return true;
      }),
    terms: Yup.boolean().test(
      'terms',
      'Please agree to our Privacy Policy, Terms of Use, and Acceptable Use Policy to continue.',
      value => value
    ),
    additionalTerms: Yup.boolean().test(
      'additionalTerms',
      'Please confirm that you are at least 13 years old to continue.',
      value => value
    )
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={PersonalDetailsSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ touched, errors, setFieldValue, values }) => {
        const emailError = touched.email && errors.email ? String(errors.email) : '';
        const phoneError = touched.phone && errors.phone ? String(errors.phone) : '';
        const emailInUse = emailError === 'This email address is already registered.';
        const phoneInUse = phoneError.indexOf('already registered') !== -1;

        return (
          <Form className="eSchoolSignupForm" noValidate>
            <div className="eSchoolSignupScreenHeader">
              <h1>Your details</h1>
              <p>Your email becomes your login. We use verification codes to confirm the account is yours.</p>
            </div>

            {gateError && <div className="alert alert-warning">{gateError}</div>}

            <div className="eRegistrationTwoCol">
              <div className="form-group">
                <label>First name</label>
                <Field
                  name="firstName"
                  placeholder="First name"
                  className={`form-control ${touched.firstName && errors.firstName ? 'is-invalid' : ''}`}
                />
                {touched.firstName && errors.firstName && (
                  <div className="eRegistrationInlineError">{String(errors.firstName)}</div>
                )}
              </div>
              <div className="form-group">
                <label>Last name</label>
                <Field
                  name="lastName"
                  placeholder="Last name"
                  className={`form-control ${touched.lastName && errors.lastName ? 'is-invalid' : ''}`}
                />
                {touched.lastName && errors.lastName && (
                  <div className="eRegistrationInlineError">{String(errors.lastName)}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Email address</label>
              <Field
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className={`form-control ${emailError ? 'is-invalid' : ''}`}
              />
              <div className={`eRegistrationFieldMessage ${emailError ? 'isError' : ''}`}>
                {emailError ? (
                  emailInUse ? (
                    <span>
                      This address already has an account. <a href="/login">Sign in</a> or{' '}
                      <a href="/resetPassword">reset your password</a>.
                    </span>
                  ) : (
                    emailError
                  )
                ) : (
                  props.emailHint || 'This email address is also your login ID.'
                )}
              </div>
            </div>

            <div className="form-group">
              <label>{isPhoneOptionalForRegistration ? 'Mobile number (optional)' : 'Mobile number'}</label>
              <div className="eRegistrationPhoneGrid">
                <Field name="phoneCode" component="select" className="form-control" aria-label="Country calling code">
                  <option value={PHONE_CODE.UK}>{PHONE_CODE.UK}</option>
                  <option value={PHONE_CODE.US}>{PHONE_CODE.US}</option>
                  <option value={PHONE_CODE.AE}>{PHONE_CODE.AE}</option>
                  <option value={PHONE_CODE.DE}>{PHONE_CODE.DE}</option>
                </Field>
                <Field
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="Mobile number"
                  className={`form-control ${phoneError ? 'is-invalid' : ''}`}
                />
              </div>
              <div className={`eRegistrationFieldMessage ${phoneError ? 'isError' : ''}`}>
                {phoneError ? (
                  phoneInUse ? (
                    <span>
                      This number is already registered to another account. Use a different one, or{' '}
                      <a href="mailto:support@squadintouch.com">ask support to move it</a>.
                    </span>
                  ) : (
                    phoneError
                  )
                ) : isPhoneOptionalForRegistration ? (
                  'SMS verification is not required for this email domain.'
                ) : (
                  'We send your verification code here by SMS.'
                )}
              </div>
            </div>

            <div className="eRegistrationTwoCol">
              <div className="form-group position-relative">
                <label>Password</label>
                <div className="input-group eRegistrationPasswordInput">
                  <Field
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                  />
                  <button
                    type="button"
                    className="eRegistrationPasswordToggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {touched.password && errors.password && (
                  <div className="eRegistrationInlineError">{String(errors.password)}</div>
                )}
              </div>
              <div className="form-group position-relative">
                <label>Confirm password</label>
                <div className="input-group eRegistrationPasswordInput">
                  <Field
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    autoComplete="new-password"
                    placeholder="Type it again"
                    className={`form-control ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid' : ''}`}
                  />
                  <button
                    type="button"
                    className="eRegistrationPasswordToggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {touched.confirmPassword && errors.confirmPassword && (
                  <div className="eRegistrationInlineError">{String(errors.confirmPassword)}</div>
                )}
              </div>
            </div>

            <div className="eRegistrationConsents">
              <label className="eRegistrationConsent">
                <input
                  type="checkbox"
                  checked={!!values.terms}
                  onChange={event => setFieldValue('terms', event.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <a href="https://www.squadintouch.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  ,{' '}
                  <a href="https://www.squadintouch.com/terms-of-use/" target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.squadintouch.com/acceptable-use-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Acceptable Use Policy
                  </a>
                  .
                </span>
              </label>
              {touched.terms && errors.terms && <div className="eRegistrationInlineError">{String(errors.terms)}</div>}

              <div className="eRegistrationAgeConsent">
                <label className="eRegistrationConsent">
                  <input
                    type="checkbox"
                    checked={!!values.additionalTerms}
                    onChange={event => setFieldValue('additionalTerms', event.target.checked)}
                  />
                  <span>I am 13 or older.</span>
                </label>
                <button type="button" className="eRegistrationUnder13Link" onClick={() => setIsUnder13HelpOpen(true)}>
                  Under 13?
                </button>
              </div>
              {touched.additionalTerms && errors.additionalTerms && (
                <div className="eRegistrationInlineError">{String(errors.additionalTerms)}</div>
              )}
            </div>

            <input
              type="text"
              name="_hp"
              tabIndex={-1}
              autoComplete="off"
              className="eRegistrationHoneypot"
              aria-hidden="true"
            />
            <div ref={turnstileContainerRef} className={`eRegistrationTurnstile ${captchaReady ? 'isReady' : ''}`} />

            <div className="eRegistrationFormActions">
              <button
                type="submit"
                className="btn btn-primary eRegistrationSubmitButton"
                disabled={isCooldown || !captchaReady}
              >
                <span>{isCooldown ? `Please wait (${props.cooldownSec}s)` : 'Send verification codes'}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
              <span>Takes about two minutes.</span>
            </div>

            <Under13HelpModal isOpen={isUnder13HelpOpen} onClose={() => setIsUnder13HelpOpen(false)} />
          </Form>
        );
      }}
    </Formik>
  );
}

function Under13HelpModal(props: { isOpen: boolean; onClose: () => void }) {
  React.useEffect(() => {
    if (!props.isOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [props.isOpen, props.onClose]);

  if (!props.isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <SimpleModal
      isOpen={true}
      title="Under 13 and joining your school?"
      showFooter={false}
      isBodyBold={false}
      onCloseClick={props.onClose}
      customClass="eRegistrationUnder13Modal"
      zIndex={1300}
    >
      <div className="eRegistrationUnder13ModalBody">
        <div className="eRegistrationUnder13Intro">
          <span aria-hidden="true">i</span>
          <p>
            If your school told you to use Squad In Touch, it has probably already created a student account for you.
            You normally don’t need to register again.
          </p>
        </div>

        <div className="eRegistrationUnder13Steps">
          <div>
            <span>1</span>
            <p>
              <strong>Use your school email address</strong>
              Enter the email address provided by your school on the password setup page.
            </p>
          </div>
          <div>
            <span>2</span>
            <p>
              <strong>Set your password</strong>
              If that email matches your student account, we’ll send you a secure link to choose a password.
            </p>
          </div>
        </div>

        <div className="eRegistrationUnder13Support">
          <strong>Need help?</strong>
          <span>
            If you can’t use your school email or the message does not arrive, contact{' '}
            <a href="mailto:support@squadintouch.com">support@squadintouch.com</a>. We’re always happy to help.
          </span>
        </div>

        <div className="eRegistrationUnder13Actions">
          <button
            type="button"
            className="eRegistrationUnder13Action eRegistrationUnder13Action--secondary"
            onClick={props.onClose}
          >
            Back to registration
          </button>
          <a
            className="eRegistrationUnder13Action eRegistrationUnder13Action--primary"
            href="/resetPassword?mode=setup&source=under13"
          >
            Set up my password
          </a>
        </div>
      </div>
    </SimpleModal>,
    document.body
  );
}
